import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/core/http';
import { IOcbAdministrator } from '@app/core/models';
import { Observable, ReplaySubject, Subject, timer } from 'rxjs';
import { map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';

@Injectable()
export class UserService {

    private static readonly environmentStorageKey = 'environmentId';
    private static readonly languageStorageKey = 'languageId';

    // Cache refresh timer (a half hour)
    private readonly refreshInterval = 1800 * 1000;

    private _loggedIn$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private userCache$: Observable<IOcbAdministrator>;
    private stop$ = new Subject<void>();

    public loggedIn$: Observable<boolean> = this._loggedIn$.asObservable();

    constructor(private auth: AuthenticationService) { }

    /**
     * Check if the current user is logged in
     */
    public get isAuthenticated(): boolean {
        return this.auth.isAuthenticated;
    }

    /**
     * Current user
     */
    public get user(): Observable<IOcbAdministrator> {
        if (!this.userCache$) {
            // Set up timer that ticks every X milliseconds
            const timer$ = timer(0, this.refreshInterval);

            this.userCache$ = timer$.pipe(
                takeUntil(this.stop$),
                switchMap(() => this.auth.getUserProfile()),
                shareReplay<IOcbAdministrator>(),
            );
        }

        return this.userCache$;
    }

    /**
     * Environment currently selected
     */
    public get currentEnvironment(): string {
        return localStorage.getItem(UserService.environmentStorageKey);
    }

    public set currentEnvironment(value: string) {
        localStorage.setItem(UserService.environmentStorageKey, value);
    }

    /**
     * User language
     */
    public get language(): string {
        return localStorage.getItem(UserService.languageStorageKey) || 'en';
    }

    public set language(value: string) {
        localStorage.setItem(UserService.languageStorageKey, value);
    }

    public hasPermission(permissionName: string) {
        return this.user.pipe(
            take(1),
            map(user => permissionName in user ? user[permissionName] : false),
        );
    }

    public hasEnvironmentPermission(environmentId: string, permissionName?: string): Observable<boolean>;
    public hasEnvironmentPermission(environmentId: string, permissionName = null): Observable<boolean> {
        return this.user.pipe(
            take(1),
            map(user => user.environments.find(env => env.environmentId === environmentId)),
            map(environment => {
                if (!environment) {
                    return false;
                }

                if (permissionName == null) {
                    return true;
                }

                if (!(permissionName in environment)) {
                    return false;
                }

                return environment[permissionName];
            }),
        );
    }

    /**
     * Remove all current user data
     */
    public logout(): void {
        // Stop the user cache reload
        this.stop$.next();
        this.userCache$ = null;

        // Clear the local storage
        this.auth.logout();
    }

    public setLoggedIn(value: boolean) {
        this._loggedIn$.next(value);
    }
}
